import React, { useState } from "react";
import { Responsive, WidthProvider, Layouts, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import type { DashboardWidget } from "../../../types/dashboard/dashboard.types";

import WidgetShell from "../widget/WidgetShell";
import WidgetRenderer from "../widget/WidgetRenderer";

const ResponsiveGridLayout = WidthProvider(Responsive);

type Props = {
    widgets: DashboardWidget[];
    layouts: Layouts;
    isEditMode: boolean; // ✅
    onLayoutChange: (current: Layout[], all: Layouts) => void;
    onBreakpointChange: (bp: string) => void;
    onRequestDelete: (widget: DashboardWidget) => void;
    selectedWidgetId?: string | null;
    onSelectWidget?: (widget: DashboardWidget) => void;
    onRequestEdit?: (widget: DashboardWidget) => void;
    onRequestAdd?: () => void;
    draftSections?: Array<{ id: string; pattern: number[] }>;
    onRequestAddToColumn?: (layout: Pick<DashboardWidget, "x" | "y" | "w" | "h"> & { draftSectionId?: string }) => void;
    onDuplicateWidget?: (widget: DashboardWidget) => void;
    onMoveWidget?: (sourceId: string, targetId: string) => void;
};


export default function DashboardGrid({
    widgets,
    layouts,
    onLayoutChange,
    onBreakpointChange,
    onRequestDelete,
    selectedWidgetId,
    onSelectWidget,
    onRequestEdit,
    onRequestAdd,
    draftSections = [],
    onRequestAddToColumn,
    onDuplicateWidget,
    onMoveWidget,
    isEditMode

}: Props) {
    const [draggingWidgetId, setDraggingWidgetId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);

    if (!widgets?.length) {
        return (
            <div className="dash2-builder-canvas">
                {draftSections.map((section, sectionIndex) => (
                    <section className="dash2-builder-row draft" key={section.id}>
                        <div className="dash2-row-toolbar">
                            <div className="dash2-row-left">
                                <button type="button" title="Rangée">
                                    <i className="ri-layout-column-line" />
                                </button>
                                <span>Rangée {sectionIndex + 1}</span>
                            </div>
                        </div>
                        <div className="dash2-builder-row-grid">
                            {section.pattern.map((width, columnIndex) => {
                                const x = section.pattern.slice(0, columnIndex).reduce((sum, item) => sum + item, 0);
                                return (
                                    <button
                                        type="button"
                                        key={`${section.id}-${columnIndex}`}
                                        className="dash2-empty-column"
                                        style={{ gridColumn: `span ${width}` }}
                                        onClick={() => onRequestAddToColumn?.({ x, y: sectionIndex * 8, w: width, h: 4, draftSectionId: section.id })}
                                    >
                                        <i className="ri-add-line" />
                                        Ajouter un widget
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                ))}
                <div className="dash2-empty-builder" onClick={onRequestAdd}>
                    <button type="button">
                        <i className="ri-add-line" />
                    </button>
                    <span>Ajouter une section ou un widget</span>
                </div>
            </div>
        );
    }

    if (isEditMode) {
        const sortedWidgets = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
        const bottomY = sortedWidgets.reduce((max, widget) => Math.max(max, widget.y + widget.h), 0);
        const rows = sortedWidgets.reduce<DashboardWidget[][]>((acc, widget) => {
            const lastRow = acc[acc.length - 1];
            if (!lastRow || Math.abs(lastRow[0].y - widget.y) > 2) {
                acc.push([widget]);
            } else {
                lastRow.push(widget);
            }
            return acc;
        }, []);

        return (
            <div className="dash2-builder-canvas">
                {rows.map((row, rowIndex) => (
                    <section className="dash2-builder-row" key={`row-${rowIndex}`}>
                        <div className="dash2-row-toolbar">
                            <div className="dash2-row-left">
                                <button type="button" title="Deplacer section">
                                    <i className="ri-drag-move-2-line" />
                                </button>
                                <button type="button" title="Colonnes">
                                    <i className="ri-layout-column-line" />
                                </button>
                                <button type="button" title="Ajouter dans la section" onClick={onRequestAdd}>
                                    <i className="ri-add-line" />
                                </button>
                            </div>
                            <div className="dash2-row-right">
                                <button type="button" title="Publie">Publie</button>
                                <button type="button" title="Modifier section">
                                    <i className="ri-pencil-line" />
                                </button>
                                <button type="button" title="Copier section">
                                    <i className="ri-file-copy-line" />
                                </button>
                                <button type="button" title="Supprimer section">
                                    <i className="ri-close-line" />
                                </button>
                            </div>
                        </div>

                        <div className="dash2-builder-row-grid">
                            {row.map((widget) => (
                                <article
                                    key={widget.id}
                                    className={`dash2-builder-widget ${selectedWidgetId === widget.id ? "selected" : ""} ${dropTargetId === widget.id ? "drop-target" : ""} ${draggingWidgetId === widget.id ? "dragging" : ""}`}
                                    style={{ gridColumn: `span ${Math.max(1, Math.min(12, widget.w || 4))}` }}
                                    onClick={() => onSelectWidget?.(widget)}
                                    draggable
                                    onDragStart={(event) => {
                                        setDraggingWidgetId(widget.id);
                                        event.dataTransfer.effectAllowed = "move";
                                        event.dataTransfer.setData("text/plain", widget.id);
                                    }}
                                    onDragEnd={() => {
                                        setDraggingWidgetId(null);
                                        setDropTargetId(null);
                                    }}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        event.dataTransfer.dropEffect = "move";
                                        if (dropTargetId !== widget.id) setDropTargetId(widget.id);
                                    }}
                                    onDragLeave={() => {
                                        if (dropTargetId === widget.id) setDropTargetId(null);
                                    }}
                                    onDrop={(event) => {
                                        event.preventDefault();
                                        const sourceId = event.dataTransfer.getData("text/plain") || draggingWidgetId;
                                        setDraggingWidgetId(null);
                                        setDropTargetId(null);
                                        if (sourceId) onMoveWidget?.(sourceId, widget.id);
                                    }}
                                >
                                    <div className="dash2-widget-mini-toolbar">
                                        <span>{widget.type}</span>
                                        <div>
                                            <button type="button" title="Ajouter" onClick={(event) => { event.stopPropagation(); onRequestAdd?.(); }}>
                                                <i className="ri-add-line" />
                                            </button>
                                            <button type="button" title="Modifier" onClick={(event) => { event.stopPropagation(); onRequestEdit?.(widget); }}>
                                                <i className="ri-pencil-line" />
                                            </button>
                                            <button type="button" title="Copier" onClick={(event) => { event.stopPropagation(); onDuplicateWidget?.(widget); }}>
                                                <i className="ri-file-copy-line" />
                                            </button>
                                            <button type="button" title="Supprimer" onClick={(event) => { event.stopPropagation(); onRequestDelete(widget); }}>
                                                <i className="ri-close-line" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="dash2-builder-widget-body">
                                        <WidgetRenderer widget={widget} />
                                    </div>
                                    <button type="button" className="dash2-add-under-widget" onClick={(event) => { event.stopPropagation(); onRequestAdd?.(); }}>
                                        <i className="ri-add-line" />
                                    </button>
                                </article>
                            ))}
                        </div>
                    </section>
                ))}

                {draftSections.map((section, sectionIndex) => {
                    const y = bottomY + sectionIndex * 8 + 2;
                    return (
                        <section className="dash2-builder-row draft" key={section.id}>
                            <div className="dash2-row-toolbar">
                                <div className="dash2-row-left">
                                    <button type="button" title="Rangée">
                                        <i className="ri-layout-column-line" />
                                    </button>
                                    <span>Nouvelle rangée</span>
                                </div>
                            </div>
                            <div className="dash2-builder-row-grid">
                                {section.pattern.map((width, columnIndex) => {
                                    const x = section.pattern.slice(0, columnIndex).reduce((sum, item) => sum + item, 0);
                                    return (
                                        <button
                                            type="button"
                                            key={`${section.id}-${columnIndex}`}
                                            className="dash2-empty-column"
                                            style={{ gridColumn: `span ${width}` }}
                                            onClick={() => onRequestAddToColumn?.({ x, y, w: width, h: 4, draftSectionId: section.id })}
                                        >
                                            <i className="ri-add-line" />
                                            Ajouter un widget
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}

                <div className="dash2-builder-bottom-add">
                    <button type="button" onClick={onRequestAdd}>
                        <i className="ri-add-line" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ResponsiveGridLayout
            className="layout dash2-grid"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={30}
            onLayoutChange={onLayoutChange}
            onBreakpointChange={onBreakpointChange}
            margin={[10, 10]}
            compactType="vertical"
            preventCollision={true}
            isResizable={isEditMode}
            isDraggable={isEditMode}
            draggableHandle={isEditMode ? ".widget-header" : undefined}


        >
            {widgets.map((widget) => (
                <div
                    key={widget.id}
                    className={isEditMode && selectedWidgetId === widget.id ? "dash2-widget-selected" : ""}
                    onMouseDown={() => {
                        if (isEditMode) onSelectWidget?.(widget);
                    }}
                >
                    <WidgetShell
                        widget={widget}
                        isEditMode={isEditMode}
                        onRequestDelete={onRequestDelete}
                    >
                        <WidgetRenderer widget={widget} />
                    </WidgetShell>

                </div>
            ))}
        </ResponsiveGridLayout>
    );
}
